import { useCallback, useRef, useState } from 'react';
import { ImageIcon, Loader2, Sparkles, Trash2, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const MAX_BYTES = 5 * 1024 * 1024;

type Status = 'idle' | 'uploading' | 'confirming' | 'done' | 'error';

interface IconPickerProps {
  /** The plugin builder session whose draft arche owns the icon. */
  sessionId: string;
  /** Current icon URL (CDN-resolved imageRef from the arche). */
  currentImageRef: string | null;
  /** Locks all controls when the parent disables editing (e.g., post-publish). */
  disabled?: boolean;
  /** Bubbled up so the parent can refresh its arche state. */
  onUpdated?: (next: { iconKey: string; imageRef: string } | null) => void;
}

/**
 * Three-mode icon attachment for arche-builder drafts:
 *  - upload from local file system
 *  - generate via icon-generator arche (deep-link with `?attachTo=archId`)
 *  - delete the current icon
 *
 * Wraps the unified pipeline; the canonical write happens server-side in
 * `services/arche-icon-pipeline.ts`. This component only handles file
 * staging, validation, and status reporting.
 */
export function IconPicker({ sessionId, currentImageRef, disabled, onUpdated }: IconPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const navigate = useNavigate();

  const isBusy = status === 'uploading' || status === 'confirming';

  const handleFile = useCallback(
    async (file: File) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setStatus('error');
        setStatusMessage('Allowed types: PNG, JPEG, WebP, SVG');
        return;
      }
      if (file.size > MAX_BYTES) {
        setStatus('error');
        setStatusMessage('File too large — 5MB max');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);

      try {
        setStatus('uploading');
        setStatusMessage('Requesting upload URL…');
        const { uploadUrl, stagingKey } = await api.getPluginBuilderIconUploadUrl(
          sessionId,
          file.type,
          file.size,
        );

        setStatusMessage('Uploading…');
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);

        setStatus('confirming');
        setStatusMessage('Promoting to canonical key…');
        const confirmed = await api.confirmPluginBuilderIcon(sessionId, stagingKey);

        setStatus('done');
        setStatusMessage(`Icon set. Variants: ${confirmed.variantSizes.join('/')}px.`);
        onUpdated?.({ iconKey: confirmed.iconKey, imageRef: confirmed.imageRef });
      } catch (err) {
        setStatus('error');
        setStatusMessage(err instanceof Error ? err.message : 'Upload failed');
      }
    },
    [sessionId, onUpdated],
  );

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate = async () => {
    // Ensure the draft arche exists before navigating; the icon-upload
    // endpoint creates it as a side-effect, but the generator deep-link
    // needs an archId in the query string.
    try {
      const { archId } = await api.getPluginBuilderIconUploadUrl(
        sessionId,
        'image/png',
        // Tiny placeholder size — the URL itself isn't used; we only need
        // the archId. The presigned URL expires in 15 minutes anyway.
        1024,
      );
      navigate(`/a/arche_icon_generator?attachTo=${encodeURIComponent(archId)}`);
    } catch (err) {
      setStatus('error');
      setStatusMessage(err instanceof Error ? err.message : 'Could not start generator flow');
    }
  };

  const handleDelete = async () => {
    try {
      setStatus('confirming');
      setStatusMessage('Removing icon…');
      await api.deletePluginBuilderIcon(sessionId);
      setPreview(null);
      setStatus('done');
      setStatusMessage('Icon removed');
      onUpdated?.(null);
    } catch (err) {
      setStatus('error');
      setStatusMessage(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const displayUrl = preview || currentImageRef;

  return (
    <Card>
      <CardContent className="pt-6 space-y-3">
        <Label>Arche Icon</Label>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl overflow-hidden border border-border flex-shrink-0">
            {displayUrl ? (
              <img src={displayUrl} alt="Arche icon" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted">
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            <p>PNG, JPEG, WebP, or SVG. Up to 1024×1024 and 5 MB.</p>
            <p className="mt-1">
              We generate the 64/128/256/512 variants automatically and serve via CDN.
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          className="hidden"
          onChange={handleFileInputChange}
        />

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || isBusy}
            onClick={() => fileInputRef.current?.click()}
          >
            {status === 'uploading' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || isBusy}
            onClick={handleGenerate}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Generate with AI
          </Button>
          {currentImageRef && (
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled || isBusy}
              onClick={handleDelete}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove
            </Button>
          )}
        </div>

        {statusMessage && (
          <div
            className={
              status === 'error'
                ? 'text-xs text-destructive'
                : status === 'done'
                  ? 'text-xs text-emerald-600 dark:text-emerald-400'
                  : 'text-xs text-muted-foreground'
            }
          >
            {statusMessage}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
